import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { Controller, useFieldArray } from "react-hook-form";
import Select from "react-select";

import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import classNames from "classnames";
import invariant from "tiny-invariant";

import PhotoRow from "../PhotoRow";

import TinyEditor from "../../../../../../components/Editor";
import FileUpload from "../../../../../../components/FileUpload";
import ImageModal from "../../../../../../components/ImageModal";
import {
    selectAgeLevels,
    selectBracketTypes,
    selectDesignations,
    selectFields,
} from "../../../../../../features/global/globalSlice";
import { useAppSelector } from "../../../../../../hooks/reduxHooks";
import {
    CONTENT_IMAGE_URL,
    MAX_FILE_SIZE,
    TOURNAMENT_LOGO_URL,
    VALID_FILE_EXTENSIONS,
} from "../../../../../../lib/globals";
import {
    defaultDateTimeFormat,
    displayFormAlert,
} from "../../../../../../lib/utils";
import {
    IDesignation,
    IField,
    IGame,
    IMasterTournament,
} from "../../../../../../types/api";
import { ITourneyMgmtTourneyInfoData } from "../../../../../../types/forms";
import { useTournamentContext } from "../../contexts/Tournament";
import { useFormHandlers } from "../../hooks/useFormHandlers";
import { reOrderPhotos } from "../../lib/reorderPhotos";
import { PhotoData } from "../../types/tournament";

const MasterTouranment = (): React.JSX.Element => {
    const {
        isCreatingTournament,
        tournament: masterTournament,
        tournamentDirectors,
        methods,
        currentPhotos,
        setCurrentPhotos,
        logo,
        setLogo,
        photos,
        setPhotos,
        setShowBracketChangeModal,
        modalOldBracketGames,
        modalDroppedGames,
        setModalChangedAgeLevel,
    } = useTournamentContext();
    const tournament = masterTournament as IMasterTournament;

    const allowedExtensions = VALID_FILE_EXTENSIONS.image.slice();
    const hasValidLogo = tournament?.logo && tournament?.logo !== "";

    const fields = useAppSelector(selectFields);
    const designations = useAppSelector(selectDesignations);
    const ageLevels = useAppSelector(selectAgeLevels);
    const brackets = useAppSelector(selectBracketTypes);

    const [modalShow, setModalShow] = useState<boolean>(false);
    const [modalContent, setModalContent] = useState<string>("");
    const [editorValue, setEditorValue] = useState(
        tournament?.description || "",
    );

    const { onChangeChildBracketType } = useFormHandlers();

    const defaultDesignation = designations.find(
        d => d.designation === tournament?.designation,
    )
        ? {
              value: designations.find(
                  d => d.designation === tournament?.designation,
              )?.id as IDesignation["id"],
              label: tournament?.designation as IDesignation["designation"],
          }
        : undefined;

    const defaultLocation = fields.find(f => f.id === tournament?.field?.id)
        ? {
              value: fields.find(f => f.id === tournament?.field?.id)
                  ?.id as IField["id"],
              label: tournament?.field?.name as IField["name"],
          }
        : undefined;

    const defaultDirector = isCreatingTournament
        ? undefined
        : tournament.tournamentDirector?.map(t => ({
              value: t.id,
              label: t.name,
          }));

    useEffect(() => {
        return monitorForElements({
            onDrop({ source, location }) {
                const destination = location.current.dropTargets[0];
                if (!destination) {
                    return;
                }

                const destinationData = destination.data as PhotoData;
                const sourceData = source.data as PhotoData;

                setCurrentPhotos(prevPhotos => {
                    invariant(prevPhotos);
                    return reOrderPhotos(
                        prevPhotos,
                        destinationData,
                        sourceData,
                    );
                });
            },
        });
    }, [currentPhotos, setCurrentPhotos]);

    const {
        fields: tournamentAgeLevels,
        append,
        remove,
    } = useFieldArray({
        control: methods.control,
        name: "ageLevels",
    });

    return (
        <>
            <Row>
                <Col>
                    <Form.Label className="mt-0">Tournament Name</Form.Label>
                    <Form.Group controlId="name">
                        <Form.Control
                            {...methods.register("name")}
                            defaultValue={
                                tournament?.name ? tournament.name : undefined
                            }
                            isInvalid={
                                methods.formState.errors.name ? true : false
                            }
                        />
                    </Form.Group>
                    {methods.formState.errors.name &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.name
                                ?.message as string,
                        })}
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Label>Tournament Description</Form.Label>
                    <Controller
                        name="description"
                        control={methods.control}
                        render={({ field }) => (
                            <TinyEditor<ITourneyMgmtTourneyInfoData>
                                {...field}
                                editorValue={editorValue}
                                setEditorValue={setEditorValue}
                                formField="description"
                                formMethods={methods}
                            />
                        )}
                    />
                    {methods.formState.errors.description &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.description
                                ?.message as string,
                        })}
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Label>Tournament Start Date</Form.Label>
                    <Form.Group controlId="startDate">
                        <DatePicker
                            selected={methods.watch(
                                "startDate",
                                tournament?.startDate
                                    ? new Date(tournament.startDate)
                                    : undefined,
                            )}
                            onChange={date =>
                                methods.setValue("startDate", date as Date, {
                                    shouldValidate: true,
                                })
                            }
                            showMonthDropdown
                            showYearDropdown
                            dateFormat="MMMM d, yyyy"
                            dropdownMode="select"
                            className="form-control"
                            id="tournamentStartDate"
                            ariaInvalid={
                                methods.formState.errors.startDate
                                    ? "true"
                                    : "false"
                            }
                        />
                    </Form.Group>
                    {methods.formState.errors.startDate &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.startDate
                                ?.message as string,
                        })}
                </Col>
                <Col>
                    <Form.Label>Tournament End Date</Form.Label>
                    <Form.Group controlId="endDate">
                        <DatePicker
                            selected={methods.watch(
                                "endDate",
                                tournament?.endDate
                                    ? new Date(tournament.endDate)
                                    : undefined,
                            )}
                            onChange={date =>
                                methods.setValue("endDate", date as Date, {
                                    shouldValidate: true,
                                })
                            }
                            showMonthDropdown
                            showYearDropdown
                            dateFormat="MMMM d, yyyy"
                            dropdownMode="select"
                            className="form-control"
                            id="tournamentEndDate"
                            ariaInvalid={
                                methods.formState.errors.endDate
                                    ? "true"
                                    : "false"
                            }
                            minDate={
                                methods.getValues("startDate")
                                    ? methods.getValues("startDate")
                                    : null
                            }
                        />
                    </Form.Group>
                    {methods.formState.errors.endDate &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.endDate
                                ?.message as string,
                        })}
                </Col>
                <Col>
                    <Form.Group controlId="designation">
                        <Form.Label>Competition Level</Form.Label>
                        <Controller
                            name="designation"
                            control={methods.control}
                            defaultValue={defaultDesignation}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    isSearchable={false}
                                    defaultValue={defaultDesignation}
                                    isClearable={true}
                                    required={false}
                                    options={designations.map(d => {
                                        return {
                                            value: parseInt(d.id.toString()),
                                            label: d.designation,
                                        };
                                    })}
                                />
                            )}
                        />
                        {methods.formState.errors.designation &&
                            displayFormAlert({
                                level: "danger",
                                text: methods.formState.errors.designation
                                    ?.message as string,
                            })}
                    </Form.Group>
                </Col>
                <Col>
                    <Form.Group controlId="poolGames">
                        <Form.Label>Pool Play Games</Form.Label>
                        <Form.Group controlId="poolGames">
                            <Form.Control
                                {...methods.register("poolGames")}
                                defaultValue={
                                    tournament?.poolGames || undefined
                                }
                                isInvalid={
                                    methods.formState.errors.poolGames
                                        ? true
                                        : false
                                }
                            />
                        </Form.Group>
                        {methods.formState.errors.poolGames &&
                            displayFormAlert({
                                level: "danger",
                                text: methods.formState.errors.poolGames
                                    ?.message as string,
                            })}
                    </Form.Group>
                </Col>
            </Row>
            <Row className={hasValidLogo ? "mt-5 mb-5" : ""}>
                {hasValidLogo && (
                    <Col xs={3}>
                        <img
                            className="tournamentLogo"
                            src={`${TOURNAMENT_LOGO_URL}${tournament?.logo}`}
                        />
                    </Col>
                )}
                <Col xs={hasValidLogo ? 9 : 12}>
                    <Form.Label className={hasValidLogo ? "mt-0" : ""}>
                        Tournament Logo
                    </Form.Label>
                    <Form.Group controlId="logo">
                        <FileUpload
                            maxFileCount={1}
                            allowedFileExtensions={allowedExtensions}
                            maxFileSize={5120000}
                            fileList={logo}
                            setFileList={setLogo}
                            fieldName="logo"
                        />
                        <Form.Label className="small">
                            Accepted file types: jpg, jpeg, png
                            <br />
                            Max file size: 5 MB
                        </Form.Label>
                        <p className="small mb-0">
                            Upload a new logo to replace current one
                        </p>
                    </Form.Group>
                    {methods.formState.errors.logo &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.logo
                                ?.message as string,
                        })}
                </Col>
            </Row>
            {tournament?.photos && (
                <Row>
                    <Col className="tournamentPhotos" md={6}>
                        <Form.Label>Current Photos</Form.Label>
                        <Table bordered striped hover>
                            <thead>
                                <tr>
                                    <th scope="col">Order</th>
                                    <th scope="col"></th>
                                    <th scope="col">Name</th>
                                    <th scope="col"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentPhotos?.map((p, idx) => {
                                    return (
                                        <PhotoRow
                                            photo={p}
                                            idx={idx}
                                            key={idx}
                                            setModalContent={setModalContent}
                                            setModalShow={setModalShow}
                                        />
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            )}

            <Row>
                <Col>
                    <Form.Label>
                        {!isCreatingTournament && "Add "}
                        Tournament Photos
                    </Form.Label>
                    <Form.Group controlId="photos">
                        <FileUpload
                            maxFileCount={5}
                            allowedFileExtensions={allowedExtensions}
                            maxFileSize={MAX_FILE_SIZE}
                            fileList={photos}
                            setFileList={setPhotos}
                            fieldName="photos"
                        />
                        <Form.Label>
                            Accepted file types: jpg, png, gif
                            <br />
                            Max file size: 8 MB
                        </Form.Label>
                    </Form.Group>
                    {methods.formState.errors.photos &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.photos
                                .message as string,
                        })}
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Label>Tournament Director</Form.Label>
                    <Form.Group controlId="director">
                        <Controller
                            name="director"
                            control={methods.control}
                            defaultValue={defaultDirector}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    isSearchable={true}
                                    isMulti={true}
                                    defaultValue={defaultDirector}
                                    options={tournamentDirectors.map(u => {
                                        return {
                                            value: parseInt(u.id.toString()),
                                            label: `${u.meta.firstName} ${u.meta.lastName} (${u.email})`,
                                        };
                                    })}
                                />
                            )}
                        />
                    </Form.Group>
                    {methods.formState.errors.director &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.director
                                ?.message as string,
                        })}
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Label>Tournament Location</Form.Label>
                    <Form.Group controlId="location">
                        <Controller
                            name="location"
                            control={methods.control}
                            defaultValue={defaultLocation}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    isSearchable={true}
                                    defaultValue={defaultLocation}
                                    options={fields.map(f => {
                                        return {
                                            value: parseInt(f.id.toString()),
                                            label: f.name,
                                        };
                                    })}
                                />
                            )}
                        />
                    </Form.Group>
                    {methods.formState.errors.location &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.location
                                ?.message as string,
                        })}
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Label>Discount Lodging Link</Form.Label>
                    <Form.Group controlId="hotelLink">
                        <Form.Control
                            {...methods.register("hotelLink")}
                            defaultValue={
                                tournament?.hotelLink
                                    ? tournament.hotelLink
                                    : undefined
                            }
                            isInvalid={
                                methods.formState.errors.hotelLink
                                    ? true
                                    : false
                            }
                        />
                    </Form.Group>
                    {methods.formState.errors.hotelLink &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.hotelLink
                                ?.message as string,
                        })}
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Label>Tournament Age Levels</Form.Label>
                    <Table className="tournamentAgeLevels">
                        <thead>
                            <tr>
                                <th scope="col">Age Level</th>
                                <th scope="col">Max # of Teams</th>
                                <th scope="col">Bracket Type</th>
                                <th scope="col">Fee</th>
                                <th scope="col"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tournamentAgeLevels
                                .sort((a, b) => {
                                    const labelA = a.ageLevel.label;
                                    const labelB = b.ageLevel.label;

                                    const numA = parseInt(labelA, 10);
                                    const numB = parseInt(labelB, 10);

                                    if (labelA === "" && labelB !== "")
                                        return 1;
                                    if (labelB === "" && labelA !== "")
                                        return -1;

                                    return numA - numB;
                                })
                                .map((ageLevel, index) => (
                                    <tr key={ageLevel.id}>
                                        <td>
                                            <Controller
                                                name={`ageLevels.${index}.ageLevel`}
                                                control={methods.control}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        isSearchable={true}
                                                        options={ageLevels
                                                            .filter(a => {
                                                                return !tournamentAgeLevels?.some(
                                                                    ta =>
                                                                        ta
                                                                            .ageLevel
                                                                            .value ===
                                                                        a.id,
                                                                );
                                                            })
                                                            .map(a => {
                                                                return {
                                                                    value: parseInt(
                                                                        a.id.toString(),
                                                                    ),
                                                                    label: a.ageLevel,
                                                                };
                                                            })}
                                                    />
                                                )}
                                            />
                                            {methods.formState.errors
                                                .ageLevels &&
                                                methods.formState.errors
                                                    .ageLevels[index]?.ageLevel
                                                    ?.label?.message &&
                                                displayFormAlert({
                                                    level: "danger",
                                                    text: methods.formState
                                                        .errors.ageLevels[index]
                                                        ?.ageLevel?.label
                                                        ?.message as string,
                                                })}
                                        </td>
                                        <td>
                                            <Form.Control
                                                {...methods.register(
                                                    `ageLevels.${index}.maxTeams`,
                                                )}
                                            />
                                            {methods.formState.errors
                                                .ageLevels &&
                                                methods.formState.errors
                                                    .ageLevels[index]?.maxTeams
                                                    ?.message &&
                                                displayFormAlert({
                                                    level: "danger",
                                                    text: methods.formState
                                                        .errors.ageLevels[index]
                                                        ?.maxTeams
                                                        ?.message as string,
                                                })}
                                        </td>
                                        <td>
                                            <Controller
                                                name={`ageLevels.${index}.bracket`}
                                                control={methods.control}
                                                render={({ field }) => (
                                                    <Select
                                                        {...field}
                                                        isSearchable={true}
                                                        options={brackets.map(
                                                            b => {
                                                                return {
                                                                    value: parseInt(
                                                                        b.bracketId.toString(),
                                                                    ),
                                                                    label: b.name,
                                                                };
                                                            },
                                                        )}
                                                        onChange={async v => {
                                                            setModalChangedAgeLevel(
                                                                ageLevel
                                                                    .ageLevel
                                                                    .label,
                                                            );
                                                            const showModal =
                                                                await onChangeChildBracketType(
                                                                    {
                                                                        newValue:
                                                                            v,
                                                                        originalBracket:
                                                                            ageLevel
                                                                                .bracket
                                                                                ?.value as number,
                                                                        tournamentId:
                                                                            tournament.id,
                                                                        fromMaster:
                                                                            true,
                                                                    },
                                                                );

                                                            if (showModal) {
                                                                setShowBracketChangeModal(
                                                                    true,
                                                                );
                                                            }

                                                            field.onChange(v);
                                                        }}
                                                    />
                                                )}
                                            />
                                            {methods.formState.errors
                                                .ageLevels &&
                                                methods.formState.errors
                                                    .ageLevels[index]?.bracket
                                                    ?.label?.message &&
                                                displayFormAlert({
                                                    level: "danger",
                                                    text: methods.formState
                                                        .errors.ageLevels[index]
                                                        ?.bracket?.label
                                                        ?.message as string,
                                                })}
                                        </td>
                                        <td>
                                            <Form.Control
                                                {...methods.register(
                                                    `ageLevels.${index}.cost`,
                                                )}
                                            />
                                            {methods.formState.errors
                                                .ageLevels &&
                                                methods.formState.errors
                                                    .ageLevels[index]?.cost
                                                    ?.message &&
                                                displayFormAlert({
                                                    level: "danger",
                                                    text: methods.formState
                                                        .errors.ageLevels[index]
                                                        ?.cost
                                                        ?.message as string,
                                                })}
                                        </td>
                                        <td>
                                            <Button
                                                type="button"
                                                className="no-margin-btn"
                                                onClick={() => remove(index)}
                                            >
                                                Remove Age Level
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </Table>
                    {modalOldBracketGames.length !== 0 &&
                        modalDroppedGames.length !== 0 && (
                            <Row>
                                <Col>
                                    <Form.Label>
                                        The following bracket games will be
                                        dropped upon saving:
                                    </Form.Label>
                                    <ul className={classNames("mb-0")}>
                                        {modalDroppedGames.map(gId => {
                                            const game =
                                                modalOldBracketGames.find(
                                                    g => g.id === gId,
                                                );

                                            const field = fields
                                                .find(
                                                    f => f.id === game?.fieldId,
                                                )
                                                ?.diamonds.find(
                                                    d => d.id === game?.diamond,
                                                );
                                            return (
                                                game && (
                                                    <li key={gId}>
                                                        {game.ageLevel.ageLevel}
                                                        ,{" "}
                                                        {defaultDateTimeFormat.format(
                                                            new Date(game.date),
                                                        )}
                                                        , {field?.name}
                                                    </li>
                                                )
                                            );
                                        })}
                                    </ul>
                                </Col>
                            </Row>
                        )}
                    <Button
                        type="button"
                        onClick={() =>
                            append(
                                {
                                    ageLevel: {
                                        value: 0,
                                        label: "",
                                    },
                                    bracket: {
                                        value: 0,
                                        label: "",
                                    },
                                    maxTeams: 0,
                                    cost: 0,
                                },
                                {
                                    focusName: `ageLevels.${
                                        methods.getValues("ageLevels")
                                            ?.length as number
                                    }.ageLevel`,
                                },
                            )
                        }
                    >
                        Add Age Level
                    </Button>
                </Col>
            </Row>
            <Row></Row>
            <ImageModal
                show={modalShow}
                image={modalContent}
                showAlt={false}
                source={`${CONTENT_IMAGE_URL}tournamentPhotos/`}
                onHide={() => setModalShow(false)}
            />
        </>
    );
};

export default MasterTouranment;
